import { createContext, useContext, useEffect, useMemo, useState } from "react";
import en from "./en";
import ar from "./ar";
import hi from "./hi";
import zh from "./zh";
import es from "./es";

/**
 * Farmsons i18n.
 *
 * To add a language (e.g. Portuguese, French):
 *   1. Create src/i18n/<code>.js mirroring en.js (a `data` section may override
 *      content from src/lib/data.js — keyed by slug/name/index).
 *   2. Register it in LANGUAGES and DICTS below.
 * No other code changes are needed. Missing keys fall back to English.
 */
export const LANGUAGES = [
  { code: "en", label: "English", dir: "ltr" },
  { code: "ar", label: "العربية", dir: "rtl" },
  { code: "hi", label: "हिन्दी", dir: "ltr" },
  { code: "zh", label: "中文", dir: "ltr" },
  { code: "es", label: "Español", dir: "ltr" },
];

const DICTS = { en, ar, hi, zh, es };
const STORAGE_KEY = "farmsons-lang";

const getPath = (obj, path) => path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return DICTS[saved] ? saved : "en";
    } catch {
      return "en";
    }
  });

  useEffect(() => {
    const meta = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];
    document.documentElement.lang = lang;
    document.documentElement.dir = meta.dir;
    try { localStorage.setItem(STORAGE_KEY, lang); } catch { /* private mode */ }
  }, [lang]);

  const value = useMemo(() => {
    const dict = DICTS[lang] || en;
    const meta = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];
    return {
      lang,
      setLang,
      languages: LANGUAGES,
      isRtl: meta.dir === "rtl",
      t: (key) => getPath(dict, key) ?? getPath(en, key) ?? key,
      /** Language-specific overrides for content that lives in lib/data.js. */
      data: dict.data || {},
    };
  }, [lang]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLang = () => useContext(LanguageContext);
